/* eslint-disable no-unknown/no-unknown, no-raw-nulls/no-raw-nulls */
import { v7 as uuidv7 } from "uuid";

import type { TaskEitherTs } from "@ps-bridge/interop";
import type {
    StatementClassifierApiDecodeResult,
    StatementClassifierClient,
    StatementClassifierClientError,
    StatementClassifierErrorCode,
    StatementClassifierGuidance,
    StatementClassifierJobId,
    StatementClassifierPreviewResult,
    StatementClassifierStatusResult,
    StatementClassifierSubmitJobResult,
    StatementClassifierSubmitWireRequest,
    StatementClassifierWorkbookFileName,
    StatementClassifierWorkbookResult,
} from "@services/clients/StatementClassifierClient";
import {
    decodeStatementClassifierPreviewResponse,
    decodeStatementClassifierRefusal,
    decodeStatementClassifierStatusResponse,
    decodeStatementClassifierSubmitJobResponse,
    decodeStatementClassifierSubmitWireRequest,
    decodeStatementClassifierWorkbookFileName,
    statementClassifierLocalError,
} from "@services/clients/StatementClassifierClient";
import {
    statementClassifierRouteForJob,
    StatementClassifierRoutes,
} from "@services/clients/StatementClassifierRoutes";
import API_URL from "@utils/config";
import {
    taskEitherChain,
    taskEitherLeft,
    taskEitherOf,
    taskEitherTryCatch,
} from "@utils/effects/branded";

type JsonResponseDecoder<T> = (input: unknown) => StatementClassifierApiDecodeResult<T>;

type RefusalFallback = {
    readonly status: number;
    readonly routeError: StatementClassifierErrorCode;
};

const DEFAULT_MEDIA_TYPE = "application/octet-stream";
const DEFAULT_WORKBOOK_FILENAME = "statement-classifier.xlsx";

const requestHeaders = (traceId: string, includeContentType: boolean): HeadersInit => {
    const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Trace-ID": traceId,
    };

    if (includeContentType) {
        headers["Content-Type"] = "application/json";
    }

    return headers;
};

const bytesToBase64 = (buffer: ArrayBuffer): string => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        const chunk = bytes.subarray(offset, offset + chunkSize);
        binary += String.fromCharCode(...chunk);
    }

    return globalThis.btoa(binary);
};

const readFileBytes = (file: File): Promise<ArrayBuffer> =>
    typeof file.arrayBuffer === "function"
        ? file.arrayBuffer()
        : new Response(file).arrayBuffer();

const readFileAsWireFile = async (file: File) => ({
    fileName: file.name,
    mediaType: file.type.trim() === "" ? DEFAULT_MEDIA_TYPE : file.type,
    byteSize: file.size,
    bytesBase64: bytesToBase64(await readFileBytes(file)),
});

const encodeSubmitRequest = async (
    files: File[],
    guidance: StatementClassifierGuidance,
): Promise<StatementClassifierSubmitWireRequest> => {
    const request = {
        guidance: guidance.text,
        businessDescription: guidance.businessDescription,
        businessExamples: guidance.businessExamples,
        personalExamples: guidance.personalExamples,
        ambiguousRules: guidance.ambiguousRules,
        extraNotes: guidance.extraNotes,
        classificationAdjustments: guidance.classificationAdjustments,
        files: await Promise.all(files.map(readFileAsWireFile)),
    };
    const decoded = decodeStatementClassifierSubmitWireRequest(request);

    if (decoded.type === "ok") {
        return decoded.value;
    }

    return Promise.reject(new Error("Statement Classifier upload request did not match the backend wire contract."));
};

const parseJson = async (response: Response): Promise<unknown> => response.json();

const networkError = (reason: Error): StatementClassifierClientError => {
    const text = `${reason.name} ${reason.message}`.toLowerCase();
    const timedOut = text.includes("abort") || text.includes("timeout") || text.includes("timed out");

    return statementClassifierLocalError(
        "networkError",
        timedOut
            ? "The statement classification request timed out or was canceled before the result was available."
            : "The statement classification service could not be reached. Try again after the service is available.",
    );
};

const responseDecodeError = (): StatementClassifierClientError =>
    statementClassifierLocalError(
        "responseDecodeError",
        "The statement classification service returned a response the client could not read.",
    );

const fallbackError = ({ status, routeError }: RefusalFallback): StatementClassifierClientError =>
    statementClassifierLocalError(
        routeError,
        `Statement classification failed with HTTP ${status}.`,
    );

const decodeOrFail = <T>(
    decoder: JsonResponseDecoder<T>,
    input: unknown,
): TaskEitherTs<StatementClassifierClientError, T> => {
    const decoded = decoder(input);
    if (decoded.type === "ok") {
        return taskEitherOf(decoded.value);
    }

    return taskEitherLeft(responseDecodeError());
};

const parseRefusal = (
    input: unknown,
    fallback: RefusalFallback,
): StatementClassifierClientError => {
    const decoded = decodeStatementClassifierRefusal(input);
    if (decoded.type === "ok") {
        return decoded.value;
    }

    return fallbackError(fallback);
};

const handleJsonResponse = <T>(
    response: Response,
    decoder: JsonResponseDecoder<T>,
    fallback: RefusalFallback,
): TaskEitherTs<StatementClassifierClientError, T> => {
    const responseFallback = {
        ...fallback,
        status: response.status,
    };
    const jsonTask = taskEitherTryCatch(
        () => parseJson(response),
        () => response.ok ? responseDecodeError() : fallbackError(responseFallback),
    );

    return taskEitherChain(jsonTask, (json) => {
        if (response.ok) {
            return decodeOrFail(decoder, json);
        }

        return taskEitherLeft(parseRefusal(json, responseFallback));
    });
};

const handleErrorResponse = (
    response: Response,
    fallback: RefusalFallback,
): TaskEitherTs<StatementClassifierClientError, never> => {
    const responseFallback = {
        ...fallback,
        status: response.status,
    };
    const jsonTask = taskEitherTryCatch(
        () => parseJson(response),
        () => fallbackError(responseFallback),
    );

    return taskEitherChain(jsonTask, (json) => taskEitherLeft(parseRefusal(json, responseFallback)));
};

const fetchJson = <T>(
    route: string,
    init: RequestInit,
    decoder: JsonResponseDecoder<T>,
    fallback: RefusalFallback,
): TaskEitherTs<StatementClassifierClientError, T> => {
    const fetchTask = taskEitherTryCatch(
        () => fetch(`${API_URL}${route}`, init),
        networkError,
    );

    return taskEitherChain(fetchTask, (response) => handleJsonResponse(response, decoder, fallback));
};

const workbookFileNameFromHeaders = (
    headers: Headers,
): StatementClassifierApiDecodeResult<StatementClassifierWorkbookFileName> => {
    const disposition = headers.get("Content-Disposition") ?? "";
    const filenameMatch = /filename="?([^";]+)"?/i.exec(disposition);
    const candidate = filenameMatch === null ? DEFAULT_WORKBOOK_FILENAME : filenameMatch[1];
    const decoded = decodeStatementClassifierWorkbookFileName(candidate);

    if (decoded.type === "ok") {
        return decoded;
    }

    const fallback = decodeStatementClassifierWorkbookFileName(DEFAULT_WORKBOOK_FILENAME);
    if (fallback.type === "ok") {
        return fallback;
    }

    return decoded;
};

export class DefaultStatementClassifierClient implements StatementClassifierClient {
    submitJob(
        files: File[],
        guidance: StatementClassifierGuidance,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierSubmitJobResult> {
        const bodyTask = taskEitherTryCatch(
            () => encodeSubmitRequest(files, guidance),
            () => statementClassifierLocalError(
                "invalidPayload",
                "The selected files could not be prepared for statement classification.",
            ),
        );

        return taskEitherChain(bodyTask, (body) =>
            fetchJson(
                StatementClassifierRoutes.submitJob,
                {
                    method: "POST",
                    credentials: "include",
                    headers: requestHeaders(uuidv7(), true),
                    body: JSON.stringify(body),
                },
                decodeStatementClassifierSubmitJobResponse,
                {
                    status: 400,
                    routeError: "invalidPayload",
                },
            ),
        );
    }

    getJobStatus(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierStatusResult> {
        return fetchJson(
            statementClassifierRouteForJob(StatementClassifierRoutes.jobStatus, jobId),
            {
                method: "GET",
                credentials: "include",
                headers: requestHeaders(uuidv7(), false),
            },
            decodeStatementClassifierStatusResponse,
            {
                status: 404,
                routeError: "unknownJob",
            },
        );
    }

    getPreview(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierPreviewResult> {
        return fetchJson(
            statementClassifierRouteForJob(StatementClassifierRoutes.preview, jobId),
            {
                method: "GET",
                credentials: "include",
                headers: requestHeaders(uuidv7(), false),
            },
            decodeStatementClassifierPreviewResponse,
            {
                status: 404,
                routeError: "unknownJob",
            },
        );
    }

    downloadWorkbook(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierWorkbookResult> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${statementClassifierRouteForJob(StatementClassifierRoutes.workbook, jobId)}`, {
                    method: "GET",
                    credentials: "include",
                    headers: requestHeaders(uuidv7(), false),
                }),
            networkError,
        );

        return taskEitherChain(fetchTask, (response) => {
            if (!response.ok) {
                return handleErrorResponse(
                    response,
                    {
                        status: response.status,
                        routeError: response.status === 404 ? "workbookUnavailable" : "downloadUnavailable",
                    },
                );
            }

            const fileName = workbookFileNameFromHeaders(response.headers);
            if (fileName.type === "error") {
                return taskEitherLeft(responseDecodeError());
            }

            return taskEitherChain(
                taskEitherTryCatch(() => response.blob(), () => responseDecodeError()),
                (content) => taskEitherOf({
                    jobId,
                    fileName: fileName.value,
                    content,
                }),
            );
        });
    }
}
